<svelte:options
  customElement={{
    tag: "oc-sheet-v1",
    extend: window.__components.extend({ asyncInit: true }),
    props: {
      open: { type: "Boolean" },
      headline: { type: "String" },
      headlineLevel: { type: "Number", attribute: "headline-level" },
      id: { type: "String", reflect: true },
      url: { type: "String" },
      base64Url: { type: "String", attribute: "base64-url" },
      hideHeader: { type: "Boolean", attribute: "hide-header" },
      hideCloseButton: { type: "Boolean", attribute: "hide-close-button" },
      fullHeight: { type: "Boolean", attribute: "full-height" },
      adaptiveAlignment: { type: "String", attribute: "adaptive-alignment" },
      maxWidthCentered: { type: "String", attribute: "max-width-centered" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      noContentPadding: { type: "Boolean", attribute: "no-content-padding" },
      discardContentOnInactive: { type: "Boolean", attribute: "discard-content-on-inactive" },
      allowedErrorStatusCodes: { type: "Array", attribute: "allowed-error-status-codes" },
      disableCloseInteractions: { type: "Boolean", attribute: "disable-close-interactions" },
    },
  }}
/>

<script lang="ts" module>
  import { safeId } from "@otto-ec/otto-components-utils/utils/safeId";
  import { useDeviceProperties } from "@otto-ec/otto-components-utils/svelte/useDeviceProperties";
  import type { Methods, Props } from "./SheetV1.types";
  import { useSlots } from "../../../common/utils/useSlots.svelte";

  import { useDocumentScrollBlock } from "./interaction/document.svelte.js";
  import { useSheetVisibility } from "./instance/visibility.svelte.js";

  import { useContentScroll } from "./interaction/scrollOverflow.svelte.js";
  import { useDragClose } from "./interaction/scrollDragClose.svelte.js";
  import { useExternalContent } from "./content/loader.svelte.js";
  import { useTrackingContext } from "./instance/tracking.svelte.js";
  import { useGlobalNavigation } from "./navigation/navigation.svelte.js";
  import { autoIdPrefix } from "./utils/props.js";
  import {
    onBackButtonClick,
    onBackdropClick,
    onCloseButtonClick,
    useSheetCloseHandler,
  } from "./interaction/closeHandler.svelte";
  import { useSheetStack } from "./instance/stack.svelte";
  import { stack } from "./stack/stack.global.svelte";
  import { useStackEvents } from "./stack/stack.events.svelte";

  import * as getters from "./apis/getters.js";
  import * as methods from "./apis/methods.js";

  useStackEvents();
  useSheetCloseHandler();
  useDocumentScrollBlock();
  useGlobalNavigation();

  const device = useDeviceProperties();
</script>

<script lang="ts">
  import { toggle } from "@otto-ec/global-resources/toggle";

  import { MediaQuery } from "svelte/reactivity";
  import {
    OnAnimationFrame,
    onElementAnimated,
    OnImmediate,
    useDomAnimationHelper,
  } from "@otto-ec/otto-components-utils/svelte/useDomAnimationHelper";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { canUseHtmlDialogElement } from "../../../common/utils/canUseHtmlDialogElement";
  import { getAriaLabelFromElements } from "../../../common/components/action/utils/getAriaLabelFromElements.ts";
  import { useKeyboardHeight } from "../../../common/utils/useKeyboardHeight.svelte.ts";

  let {
    open = false,
    headline,
    headlineLevel = 2,
    id = safeId(undefined, autoIdPrefix),
    url,
    base64Url,
    hideHeader = false,
    hideCloseButton = false,
    hideBackButton = false,
    fullHeight = false,
    adaptiveAlignment = "right",
    maxWidthCentered = "var(--oc-base-breakpoint-l)",
    ocAriaLabel,
    noContentPadding = false,
    allowedErrorStatusCodes = [],
    discardContentOnInactive = false,
    disableCloseInteractions = false,
  }: Props = $props();

  const Host = $host<HTMLOcSheetV1Element>();

  /*                                          */
  /*                                                  */
  /*                     */
  /*                                                  */
  /**/
  /*              */
  /*                                          */
  /*  */

  const slots = useSlots(Host);

  let dialogElement = $state<HTMLDialogElement | HTMLDivElement>();

  const onAnimate = onElementAnimated(() => dialogElement);
  const [visibilityState, setVisibility] = useDomAnimationHelper({
    isDisplay: [OnImmediate, onAnimate],
    isVisible: [OnAnimationFrame, OnAnimationFrame],
    /*                                                                          */
    isShown: [onAnimate, OnImmediate],
  });

  const instanceState = useSheetStack(
    {
      id: () => id,
      open: () => open,
      activation: (isActive) => (open = isActive),
    },
    visibilityState,
    setVisibility,
  );

  useSheetVisibility(Host, visibilityState, () => dialogElement as HTMLDialogElement);

  /*                                                    */
  const contentScroll = useContentScroll();

  /*                                    */
  const externalContentUrl = $derived(base64Url ? atob(base64Url) : url);
  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");

  const isLargeViewPortQuery = new MediaQuery("min-width: 480px");

  const dragClose = useDragClose(
    contentScroll,
    isLargeViewPortQuery,
    () => disableCloseInteractions,
  );

  const externalContent = useExternalContent(
    Host,
    {
      url: () => externalContentUrl,
      allowedCodes: () => allowedErrorStatusCodes,
      invalidateOnInactive: () => discardContentOnInactive,
    },
    instanceState,
  );

  /*                                                        */
  $effect(() => {
    instanceState.isReady =
      toggle.get("oc_sheet_v1_disable_wait_for_content", false) ||
      !externalContentUrl ||
      externalContent.isApplied ||
      !!externalContent.rawError;
  });

  /*                                                    */
  useTrackingContext({ sheetId: () => id }, visibilityState, externalContent);

  /*                                                                 */
  /*                                            */
  const hasBackButton = $derived(stack.contentChange && !hideBackButton);

  /*                                                                  */
  /*                                                                     */
  export const back: Methods["back"] = methods.sheetGoBack;
  export const close: Methods["close"] = methods.sheetClose;
  export const isContentApplied: Methods["isContentApplied"] = () => externalContent.isApplied;

  /*                               */
  export const getContent: Methods["getContent"] = getters.getContent.bind(null, Host);
  export const getHeader: Methods["getHeader"] = getters.getHeader.bind(null, Host);
  export const getActions: Methods["getActions"] = getters.getActions.bind(null, Host);

  const ariaLabel = $derived.by(() => {
    if (ocAriaLabel) return ocAriaLabel;
    if (!open) return undefined; /*                                                      */

    let headlineSlot = Host.shadowRoot?.querySelector('slot[name="headline"]');
    return headlineSlot ? getAriaLabelFromElements(headlineSlot) : undefined;
  });

  let keyboardHeight = useKeyboardHeight();
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="sheet"
  class:sheet--open={visibilityState.isVisible}
  class:sheet--no-transition={stack.contentChange}
  class:sheet--center={adaptiveAlignment === "center"}
  class:sheet--left={adaptiveAlignment === "left"}
  class:sheet--no-header={hideHeader}
  class:sheet--no-drag-indicator={disableCloseInteractions}
  class:sheet--full-height={fullHeight}
  class:sheet--is-touchable={device.isTouchable}
  class:sheet--no-content-padding={noContentPadding}
  style:--keyboard-height={keyboardHeight.value}
  style:--width={adaptiveAlignment === "center" && maxWidthCentered
    ? maxWidthCentered
    : "var(--oc-base-breakpoint-l)"}
  onclick={!disableCloseInteractions ? onBackdropClick(visibilityState) : undefined}
>
  <svelte:element
    this={canUseHtmlDialogElement ? "dialog" : "div"}
    bind:this={dialogElement}
    class="sheet__sheet"
    class:sheet__sheet--loaded={externalContent.isApplied}
    style={dragClose.sheetDragStyle}
    aria-label={ariaLabel}
  >
    <!--SHEET HEADER-->
    <div
      class="sheet__header"
      class:sheet__header--with-shadow={contentScroll.isScrolling && !hideHeader}
      class:sheet__header--no-headline={hideHeader}
      use:dragClose.headerEvents
    >
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      {#if hasBackButton}
        <oc-icon-button-v3
          onclick={() => !disableCloseInteractions && onBackButtonClick()}
          icon="arrow-left"
          elevation-level={hideHeader ? "above" : undefined}
          class="sheet__back-button"
          oc-aria-label="zurück"
        ></oc-icon-button-v3>
      {/if}

      <div class="sheet__headline-outer">
        <slot name="headline" id="dialog-headline">
          {#if headline}
            <svelte:element this={headlineElement} class="sheet__headline">
              <!-- eslint-disable-next-line svelte/no-at-html-tags -->
              {@html headline}
            </svelte:element>
          {/if}
        </slot>
      </div>
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      {#if !disableCloseInteractions}
        <oc-icon-button-v3
          onclick={() => !disableCloseInteractions && onCloseButtonClick()}
          class:sheet__close-button--hidden={hideCloseButton}
          icon="close"
          elevation-level={hideHeader ? "above" : undefined}
          class="sheet__close-button"
          oc-aria-label="Dialog schließen"
        ></oc-icon-button-v3>
      {/if}
    </div>
    <!--SHEET CONTENT-->
    <div
      class="sheet__content"
      class:sheet__content--no-scrolling={dragClose.isDragging}
      use:contentScroll.scrollPosition
      use:dragClose.contentEvents
      bind:clientHeight={contentScroll.contentHeight}
    >
      <!-- Display normal slot for content or error content (from response) -->
      {#if !externalContent.rawError || externalContent.errorContent}
        <!-- CONTENT or ERROR CONTENT SLOT -->
        <slot />
      {:else}
        <!-- LOADING-ERROR SLOT -->
        <div class="sheet_loading-error">
          <slot name="loading-error"></slot>
        </div>
      {/if}
    </div>

    <!--SHEET FOOTER-->
    {#if slots.actions}
      <div
        class="sheet__footer"
        class:sheet__footer--with-shadow={contentScroll.hasContentOverflow &&
          !contentScroll.isScrollEnd}
      >
        <div class="sheet__actions">
          <slot name="actions" />
        </div>
      </div>
    {/if}
  </svelte:element>
</div>
<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "./SheetV1.center" as center;
  :global {
    :host {
      position: absolute; /*                                                       */
      pointer-events: auto; /*                                                                       */
    }

    ::slotted([slot="headline"]) {
      /*                                    */
      margin-top: 4px !important;
      margin-bottom: 16px !important;
    }

    .sheet {
      /*                                           */
      --oc-snackbar-container-width: 100%;
      position: fixed;
      bottom: 0;
      right: 0;
      z-index: 9001;
      overscroll-behavior-y: none;

      &__sheet {
        all: unset;
        position: fixed;
        overflow: auto; /*                                 */
        right: 0;
        bottom: var(--keyboard-height, 0);
        left: 0;
        top: initial;
        max-height: calc(
          #{tokens.$oc-component-sheet-bottom-max-height-percentage} *
            1vh - var(--keyboard-height, 0)
        );
        max-height: calc(
          #{tokens.$oc-component-sheet-bottom-max-height-percentage} *
            1svh - var(--keyboard-height, 0)
        );
        display: flex;
        flex-direction: column;
        background: tokens.$oc-component-sheet-content-default-background-color;
        transform: translateY(100%);
        will-change: transform;
        transition: transform tokens.$oc-component-sheet-show-duration
          tokens.$oc-component-sheet-show-easing;
        border-radius: tokens.$oc-component-sheet-border-radius
          tokens.$oc-component-sheet-border-radius 0 0;
      }

      dialog.sheet__sheet {
        overflow: auto;
      }

      &__header {
        position: relative;
        z-index: 1;
        display: flex;
        gap: tokens.$oc-component-sheet-header-gap-x;
        align-items: flex-start;
        padding: 20px 12px 0 16px;
        min-height: 44px; /*                                                  */
        box-sizing: content-box;
        flex-shrink: 0;
        background: tokens.$oc-component-sheet-header-background-color;
        border-bottom: tokens.$oc-component-sheet-header-conditional-border-bottom-width solid
          transparent;

        &--with-shadow {
          border-color: tokens.$oc-component-sheet-header-conditional-border-bottom-color;
        }

        &--no-headline {
          padding: 12px 12px 0 12px;

          position: absolute;
          left: 0;
          right: 0;
          top: 0;

          pointer-events: none;

          .sheet__back-button,
          .sheet__close-button {
            pointer-events: auto;
          }
        }
      }

      &__headline-outer {
        flex: 1 0;
        pointer-events: none;
        padding-left: var(--title-spacing, 0);
      }

      &__headline {
        flex: 1 1 auto;
        color: tokens.$oc-component-sheet-header-title-color;
        font: tokens.$oc-component-sheet-header-title-font;
        /*                                                                                */
        margin-top: 4px;
        margin-bottom: tokens.$oc-semantic-spacing-100;
        user-select: none;
      }

      &__back-button {
        margin-left: -4px;
      }

      &__close-button {
        &--hidden {
          /*                                 */
          width: 0;
          height: 0;
          display: block;
          overflow: hidden;
        }
      }

      &__content {
        flex-grow: 0;
        overflow: auto;
        overscroll-behavior: contain;

        transform: translateY(0); /*                   */
        padding: tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-100
          tokens.$oc-semantic-spacing-100;

        box-sizing: content-box;
        background-color: var(
          --content-background-color,
          tokens.$oc-component-sheet-content-default-background-color
        );

        &--no-scrolling {
          overflow: hidden;
        }
      }

      &__footer {
        padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-100 30px
          tokens.$oc-semantic-spacing-100;
        border-top: none;

        &--with-shadow {
          background-color: tokens.$oc-component-sheet-action-bar-background-color;
          border-color: tokens.$oc-component-sheet-action-bar-conditional-border-top-color;
        }
      }
    }

    /*       */
    .sheet:not(dialog):before {
      position: fixed;
      content: "";
      right: 0;
      bottom: 0;
      background-color: tokens.$oc-component-sheet-scrim-color;
      opacity: 0;
      transition: opacity tokens.$oc-component-sheet-show-duration
        tokens.$oc-component-sheet-show-easing;
    }

    /*                                                            */
    .sheet:has(dialog):before {
      display: none;
    }
    dialog.sheet__sheet::backdrop {
      opacity: 0;
      background-color: tokens.$oc-component-sheet-scrim-color;
      transition: opacity tokens.$oc-component-sheet-hide-duration
        tokens.$oc-component-sheet-hide-easing;
    }

    /*        */
    .sheet--open {
      inset: 0;

      .sheet__sheet {
        transform: translate3d(0, 0, 0);
      }

      /*            */
      &.sheet:not(dialog):before {
        left: 0;
        top: 0;
        opacity: 1;
        transition: opacity tokens.$oc-component-sheet-show-duration
          tokens.$oc-component-sheet-show-easing;
      }
      dialog.sheet__sheet::backdrop {
        opacity: 1;
      }
    }

    .sheet--no-header {
      .sheet__header {
        background: none;
      }

      .sheet__headline {
        display: none;
      }

      .sheet__back-button {
        margin-top: 0;
      }

      .sheet__content {
        padding-top: tokens.$oc-semantic-spacing-relative-100;
      }
    }

    .sheet--full-height {
      .sheet__sheet {
        height: calc(80vh - var(--keyboard-height));
        height: calc(80dvh - var(--keyboard-height));
      }

      .sheet__content {
        flex-grow: 1;
      }
    }

    .sheet--is-touchable {
      .sheet__header::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        margin: auto;
        top: tokens.$oc-component-sheet-drag-indicator-spacing-top;
        width: tokens.$oc-component-sheet-drag-indicator-width;
        height: tokens.$oc-component-sheet-drag-indicator-height;
        border-radius: tokens.$oc-component-sheet-drag-indicator-border-radius;
        background-color: tokens.$oc-component-sheet-drag-indicator-color;
      }
    }

    .sheet--no-content-padding {
      .sheet__content {
        padding: 0;
      }
    }

    .sheet--no-drag-indicator {
      .sheet__header::after {
        content: none;
      }
    }

    /*                                     */
    /*                                                                            */
    @media (min-width: 480px) {
      .sheet {
        &__sheet {
          top: 0;
          left: initial;
          width: tokens.$oc-component-sheet-side-width; /*              */
          max-height: none;
          transform: translateX(100%) translateZ(0);
          will-change: transform;
          border-radius: tokens.$oc-component-sheet-border-radius
            tokens.$oc-component-sheet-border-radius;
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
        }
      }

      .sheet--left {
        .sheet__sheet {
          right: initial;
          left: 0;
          transform: translateX(-100%) translateZ(0);
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          border-bottom-right-radius: tokens.$oc-component-sheet-border-radius;
          border-top-right-radius: tokens.$oc-component-sheet-border-radius;
        }
      }

      .sheet--open {
        .sheet__sheet {
          /*                                                                                                 */
          /*                                      */
          transform: translateX(0) translateZ(0) !important;
        }

        .sheet__header {
          /*                                                                     */
          pointer-events: none;
        }

        .sheet__back-button,
        .sheet__close-button {
          /*                                               */
          pointer-events: all;
        }
      }
      .sheet--full-height {
        .sheet__sheet {
          height: auto;
        }
      }
      .sheet--is-touchable {
        .sheet__header::after {
          content: none;
        }
      }
    }

    /*          */
    @media (prefers-reduced-motion: no-preference) {
      .sheet:not(.sheet--no-transition) {
        &:before {
          transition: opacity ease 0.3s;
        }

        .sheet__sheet {
          transition: transform tokens.$oc-component-sheet-show-duration
            tokens.$oc-component-sheet-show-easing;
        }

        .sheet__header {
          transition: border-color tokens.$oc-component-sheet-conditional-border-transition-duration
            tokens.$oc-component-sheet-conditional-border-transition-easing;
        }

        .sheet__footer {
          transition: border-color tokens.$oc-component-sheet-conditional-border-transition-duration
            tokens.$oc-component-sheet-conditional-border-transition-easing;
        }
      }

      .sheet--no-transition {
        .sheet__sheet {
          transition: none 0s linear;
          will-change: unset;
          &::backdrop,
          &::before {
            transition: none 0s linear;
            will-change: unset;
          }
        }
      }
    }

    @media (prefers-reduced-motion: reduce) {
      * {
        transition: none !important;
        will-change: unset !important;
        &::backdrop,
        &::before {
          transition: none !important;
          will-change: unset !important;
        }
      }
    }

    @include center.center;

    /*                                                                                                                                         */
    @include mixins.breakpoint-from-m(false, false) {
      .sheet--full-height {
        .sheet__content {
          background-color: tokens.$oc-component-sheet-content-default-background-color;
        }
      }
    }
  }
</style>
